import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { useAuth } from "../../../../services/useAuth";
import axios from "axios";

import { MdExpandMore } from "react-icons/md";
import { MdOutlineChevronRight } from "react-icons/md";

import navigation, { menuItems, getMenuItems } from "../../../../_nav";

import styles from "./Navigation.module.scss";
import NavSubItems from "./NavSubItems";
import { FiLayers } from "react-icons/fi";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const treeContainsUrl = (items, url) => {
  if (!items?.length || !url) return false;
  return items.some(
    (item) => item.url === url || treeContainsUrl(item.items, url),
  );
};

const collectExpandedGroupIds = (items, url, ids = []) => {
  if (!items?.length || !url) return ids;
  items.forEach((item) => {
    if (item.items?.length && treeContainsUrl(item.items, url)) {
      ids.push(item.id);
      collectExpandedGroupIds(item.items, url, ids);
    }
  });
  return ids;
};

const isNavRouteActive = (itemUrl, routeName) => {
  if (!itemUrl || !routeName) return false;
  return itemUrl === routeName;
};

const mapTreeActive = (items, routeName) => {
  if (!items?.length) return items;
  return items.map((item) => {
    const children = mapTreeActive(item.items, routeName);
    const childActive = children?.some((c) => c.active);
    const selfActive = isNavRouteActive(item.url, routeName);
    const active = item.items?.length ? childActive : selfActive;
    return { ...item, active, items: children };
  });
};

const collectDescendantGroupIds = (item) => {
  if (!item?.items?.length) return [];
  const ids = [];
  item.items.forEach((child) => {
    if (child.items?.length) {
      ids.push(child.id);
      ids.push(...collectDescendantGroupIds(child));
    }
  });
  return ids;
};

// Helper function to determine user type based on new and old logic
const getUserType = (user) => {
  if (!user || !user.role) {
    return "user";
  }

  // Check role first
  const role = user.role.toLowerCase();

  if (role === "admin") return "admin";
  if (role === "employee") return "employee";
  if (role === "company") return "company";
  if (role === "dealer") return "dealer";

  // For users with role "user" - check if they have admin privileges
  if (role === "user") {
    // Only users with isAdmin: true can access admin-level features
    if (user.isAdmin === true) {
      // Check their assignment to determine their admin type
      if (user.companyId) return "company";
      if (user.dealerId) return "dealer";
      // Fallback for users with admin privileges but no clear assignment
      if (user.assignedType === "company") return "company";
      if (user.assignedType === "dealer") return "dealer";
      if (user.assignedType === "employee") return "employee";
    }
    // Regular users (isAdmin: false) get basic user access
    return "user";
  }

  // Old logic: Check assignedType for backward compatibility (only for admin users)
  if (user.isAdmin === true && user.assignedType) {
    const assignedType = user.assignedType.toLowerCase();
    if (assignedType === "company") return "company";
    if (assignedType === "dealer") return "dealer";
    if (assignedType === "employee") return "employee";
  }

  // Fallback to user for any other case
  return "user";
};

export default function Navigation(props) {
  const [height, setHeight] = useState("0px");
  const [navItems, setNavItems] = useState([]);
  const [activeMenu, setActiveMenu] = useState("");
  const [expandedSubMenus, setExpandedSubMenus] = useState(() => new Set());
  const { getUser } = useAuth();
  const user = getUser();
  const location = useLocation();

  const elementsRef = useRef([]);
  const sidebarRef = useRef(null);

  const refreshMenuHeight = (menuId) => {
    if (!menuId) return;
    setTimeout(() => {
      const element = elementsRef.current[menuId];
      if (element) setHeight(`${element.scrollHeight}px`);
    }, 50);
  };

  useEffect(() => {
    if (!user) {
      return;
    }

    // Use getMenuItems function if available (filters by actual user's modules)
    // Otherwise fall back to static menuItems by user type
    const userType = getUserType(user);
    const roleBasedItems = getMenuItems
      ? getMenuItems(user) // Use dynamic filtering based on user's modules
      : menuItems[userType] || menuItems.user || menuItems.admin;

    console.log("[Navigation] roleBasedItems:", roleBasedItems);
    console.log("[Navigation] user:", user);

    // Check if we have the new functional grouping structure or old structure
    const hasNewStructure =
      roleBasedItems &&
      (roleBasedItems.productManagement ||
        roleBasedItems.salesManagement ||
        roleBasedItems.productionManagement ||
        roleBasedItems.reportsManagement ||
        roleBasedItems.userManagement);
    const hasOldStructure =
      roleBasedItems &&
      (roleBasedItems.deviceManagement || roleBasedItems.customerManagement);

    if (!roleBasedItems || (!hasNewStructure && !hasOldStructure)) {
      console.warn("[Navigation] No menu items found for user:", user);
      return;
    }

    const processMenuItems = async () => {
      // Helper function to resolve dynamic URLs
      const resolveUrl = async (url, itemUser) => {
        if (typeof url === "function") {
          url = url(itemUser);
        }

        // If URL contains :dealerCode and user is a dealer, fetch dealer code
        if (url && url.includes(":dealerCode") && itemUser?.dealerId) {
          try {
            const token = localStorage.getItem("rovionToken");
            const response = await axios.get(`${API_URL}/api/dealers`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const dealer = response.data.find(
              (d) =>
                d._id === itemUser.dealerId ||
                String(d._id) === String(itemUser.dealerId),
            );
            if (dealer?.code) {
              return url.replace(":dealerCode", dealer.code);
            }
          } catch (error) {
            console.error("Error fetching dealer:", error);
          }
        }
        return url;
      };

      const processItems = async (items) => {
        if (!items || !Array.isArray(items)) return [];
        return await Promise.all(
          items.map(async (item) => ({
            ...item,
            url: item.url ? await resolveUrl(item.url, user) : undefined,
            id: uuid(),
            items: item.items ? await processItems(item.items) : undefined,
          })),
        );
      };

      // Use _nav structure if available, otherwise use roleBasedItems
      let processedNav = [];

      // Check if we should use _nav structure (new module-based structure)
      if (navigation && Array.isArray(navigation)) {
        console.log("[Navigation] Using _nav structure");
        processedNav = await Promise.all(
          navigation.map(async (navSection) => {
            let items = navSection.items;

            // If items is a function, call it with user
            if (typeof items === "function") {
              items = items(user);
              console.log("[Navigation] Items function returned:", items);
            }

            // Process items if they exist
            const processedItems = items ? await processItems(items) : [];

            return {
              category: navSection.category,
              items: processedItems,
            };
          }),
        );

        // Filter out categories with no items (but keep sections without category like home page)
        processedNav = processedNav.filter(
          (section) =>
            !section.category || (section.items && section.items.length > 0),
        );
      } else {
        // Fallback to old structure
        console.log("[Navigation] Using old structure (roleBasedItems)");

        // Process device management items (old structure)
        const deviceManagementItems = roleBasedItems.deviceManagement
          ? await processItems(roleBasedItems.deviceManagement)
          : [];

        // Process customer management items (old structure)
        const customerManagementItems = roleBasedItems.customerManagement
          ? await processItems(roleBasedItems.customerManagement)
          : [];

        processedNav = [
          {
            category: "Ürün Yönetimi",
            items: deviceManagementItems,
          },
          {
            category: "Müşteri Yönetimi",
            items: customerManagementItems,
          },
        ];

        // Filter out categories with no items
        processedNav = processedNav.filter(
          (section) => section.items && section.items.length > 0,
        );
      }

      // Always add home page at the beginning
      processedNav = [
        // {
        //   items: [
        //     {
        //       name: "Ana Sayfa",
        //       url: "/",
        //       icon: React.createElement(FiLayers),
        //       id: uuid(),
        //     },
        //   ],
        // },
        ...processedNav,
      ];

      console.log("[Navigation] Final processedNav:", processedNav);
      setNavItems(processedNav);
      handleActiveItem();
    };

    processMenuItems();
  }, [
    user?.role,
    user?.modules,
    user?.pages,
    user?.companyId,
    user?.dealerId,
    user?.assignedType,
    user?.assignedId,
  ]);

  useEffect(() => {
    // Reset active states when route changes
    const cleanup = () => {
      setHeight("0px");
      setActiveMenu("");
    };

    window.addEventListener("hashchange", cleanup);
    return () => window.removeEventListener("hashchange", cleanup);
  }, []);

  const handleActiveItem = (path = null) => {
    if (!navItems.length) return;

    const routeName =
      path ||
      location.pathname ||
      window.location.hash.replace("#", "") ||
      "/";

    let nextActiveMenu = "";
    const nextExpanded = new Set();

    const newNavItems = navItems.map((section) => ({
      ...section,
      items: section.items?.map((menu) => {
        const subMenus = mapTreeActive(menu.items, routeName);
        const isMenuActive = menu.url === routeName;
        const isActive =
          isMenuActive || treeContainsUrl(subMenus, routeName);

        if (isActive && menu.items?.length) {
          nextActiveMenu = menu.id;
          collectExpandedGroupIds(subMenus, routeName).forEach((id) =>
            nextExpanded.add(id),
          );
        }

        return {
          ...menu,
          active: isActive,
          items: subMenus,
        };
      }),
    }));

    setExpandedSubMenus(nextExpanded);
    setActiveMenu(nextActiveMenu);
    setNavItems(newNavItems);

    if (nextActiveMenu) {
      refreshMenuHeight(nextActiveMenu);
    } else {
      setHeight("0px");
    }
  };

  useEffect(() => {
    if (!props.toggledMenu && !props?.isMouseOver) {
      // Only reset active states, don't replace navItems completely
      setNavItems((prevNavItems) => {
        return prevNavItems.map((section) => ({
          ...section,
          items: section.items?.map((item) => ({
            ...item,
            active: false,
            items: item.items?.map((subItem) => ({
              ...subItem,
              active: false,
            })),
          })),
        }));
      });
      setHeight("0px");
    } else {
      handleActiveItem();
    }
  }, [props.toggledMenu, props.isMouseOver, location.pathname]);

  useEffect(() => {
    if (navItems.length) handleActiveItem();
  }, [location.pathname, navItems.length]); // eslint-disable-line

  const collapseMenu = (id, active) => {
    if (!props?.toggledMenu && !props.isMouseOver) return;

    const newNavItems = navItems.map((section) => ({
      ...section,
      items: section.items?.map((item) => ({
        ...item,
        active: item.id === id ? active : false,
      })),
    }));

    if (activeMenu === id) {
      setNavItems(newNavItems);
      if (!active) {
        setHeight("0px");
        setActiveMenu("");
        setExpandedSubMenus(new Set());
      } else {
        refreshMenuHeight(id);
      }
      return;
    }

    // Opening a different top-level section: close nested groups
    if (active) {
      setExpandedSubMenus(new Set());
    }

    setActiveMenu(active ? id : "");
    setNavItems(
      newNavItems.map((section) => ({
        ...section,
        items: section.items?.map((item) => ({
          ...item,
          active: item.id === id && active,
        })),
      })),
    );

    if (active) {
      refreshMenuHeight(id);
    } else {
      setHeight("0px");
    }
  };

  const toggleSubGroup = (groupItem, siblingGroups = []) => {
    setExpandedSubMenus((prev) => {
      const next = new Set(prev);
      const wasOpen = next.has(groupItem.id);

      siblingGroups.forEach((sibling) => {
        if (sibling.id === groupItem.id) return;
        next.delete(sibling.id);
        collectDescendantGroupIds(sibling).forEach((gid) => next.delete(gid));
      });

      if (wasOpen) {
        next.delete(groupItem.id);
        collectDescendantGroupIds(groupItem).forEach((gid) => next.delete(gid));
      } else {
        next.add(groupItem.id);
      }

      return next;
    });
    refreshMenuHeight(activeMenu);
  };

  const renderIcon = (IconComponent) => {
    if (!IconComponent) return null;

    // If it's a React component (function/class)
    if (typeof IconComponent === "function") {
      return <IconComponent />;
    }

    // If it's already a React element
    if (React.isValidElement(IconComponent)) {
      return IconComponent;
    }

    return null;
  };

  return (
    <div className={`${styles["navigation-container"]} pb-4`} ref={sidebarRef}>
      {navItems.map((nav, index) => (
        <div key={index}>
          {nav?.category && (
            <div className={styles["navigation-container__category"]}>
              {!(props.toggledMenu || props.isMouseOver) ? "..." : nav.category}
            </div>
          )}

          {Array.isArray(nav.items) &&
            nav.items.map((itemMenu, indexMenu) => (
              <div key={indexMenu}>
                <ul
                  className={`${styles["navigation-container__header"]} ${
                    itemMenu.active &&
                    `${styles["navigation-container__header--active"]}`
                  } ${
                    !props.toggledMenu &&
                    !props.isMouseOver &&
                    `${styles["navigation-container__header--reset-justify"]}`
                  }`}
                >
                  <li
                    className={styles["navigation-container__header__list"]}
                    onClick={() =>
                      collapseMenu(itemMenu?.id, !itemMenu?.active)
                    }
                  >
                    <Link
                      to={
                        itemMenu.url ||
                        (itemMenu.items?.length ? "#" : "/")
                      }
                      target={itemMenu?.target || "_self"}
                      onClick={(e) => {
                        if (itemMenu.items?.length && !itemMenu.url) {
                          e.preventDefault();
                        }
                        props.onClose?.({
                          hasChildrens: itemMenu?.items?.length > 0,
                        });
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-1">
                        <div className="d-flex align-items-center justify-content-between gap-1">
                          <span
                            className={
                              styles["navigation-container__header__list--icon"]
                            }
                          >
                            {renderIcon(itemMenu.icon)}
                          </span>

                          {(props.toggledMenu || props.isMouseOver) && (
                            <span
                              className={
                                styles[
                                  "navigation-container__header__list--title"
                                ]
                              }
                              onClick={() =>
                                props.onClose({
                                  hasChildrens: itemMenu?.items?.length > 0,
                                })
                              }
                            >
                              {itemMenu.name}
                            </span>
                          )}
                        </div>

                        {itemMenu?.items?.length > 0 &&
                          (props.toggledMenu || props.isMouseOver) && (
                            <span
                              className={
                                styles[
                                  "navigation-container__header__list--expand"
                                ]
                              }
                            >
                              {itemMenu?.active ? (
                                <MdExpandMore />
                              ) : (
                                <MdOutlineChevronRight />
                              )}
                            </span>
                          )}
                      </div>
                    </Link>

                    {itemMenu?.items?.length > 0 && (
                      <div
                        className={`${styles["navigation-container__content"]}`}
                        style={{
                          height: activeMenu === itemMenu?.id ? height : "0px",
                        }}
                      >
                        <ul
                          ref={(el) => (elementsRef.current[itemMenu.id] = el)}
                          className={
                            styles["navigation-container__content__ul"]
                          }
                        >
                          <NavSubItems
                            items={itemMenu.items}
                            expandedSubMenus={expandedSubMenus}
                            onToggleGroup={toggleSubGroup}
                            onClose={props.onClose}
                            showLabels={
                              props.toggledMenu || props.isMouseOver
                            }
                          />
                        </ul>
                      </div>
                    )}
                  </li>
                </ul>
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}
